import PrimaryButton from "components/common/buttons/PrimaryButton";
import { DialogBody, DialogHeader } from "components/common/dialogs/GenericDialog";
import CheckoutItems from "components/checkout/CheckoutItems";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useLocale } from "lib/hooks/useLocale";
import React, { useMemo } from "react";
import { Warning, X } from "@phosphor-icons/react";
import { useItemEdit } from "lib/hooks/useItemEdit";
import { useSearchParams } from "react-router-dom";
import { errorToast } from "lib/utils/toasters";

const CombinedProductsRemovalDialog = () => {
  const { t } = useLocale();
  const {
    state: { checkoutItems, combinedProductRemovalItems, billing },
    actions: { setCheckoutModal },
  } = useCheckoutContext();
  const [searchParams] = useSearchParams();

  const isCartPage = searchParams.get("page")?.toLowerCase() === "cart"
  
  const selectedCheckoutItem = useMemo(
    () => checkoutItems?.find((item) => item.item_id === combinedProductRemovalItems?.selectedItem?.id),
    [checkoutItems, combinedProductRemovalItems],
  );

  const removableCheckoutItems = useMemo(
    () =>
      checkoutItems?.filter((item) =>
        combinedProductRemovalItems?.removableVariantIds?.some(
          (variant) => variant?.variant_id === item?.variant_id,
        ),
      ),
    [checkoutItems, combinedProductRemovalItems],
  );
  
  const { handleItemDeleteSubmit, isLoading } = useItemEdit({
    item_id: selectedCheckoutItem?.item_id,
    quantity: selectedCheckoutItem?.quantity,
    availableQuantity: selectedCheckoutItem?.availableQuantity,
    isCartItem: isCartPage ,
    variant_id: selectedCheckoutItem?.variant_id,
    billing,
  });

  const isLastItem = useMemo(() => {
    const nonFreebieItems = checkoutItems?.filter((item) => !item.is_freebie);

    if (checkoutItems.length <= 1) {
      return true;
    }
    if (nonFreebieItems.length - removableCheckoutItems?.length === 1) {
      return true;
    }
    return false;
  }, [checkoutItems, removableCheckoutItems]);

  const handleRemoveItems = async () => {
    try {
      await handleItemDeleteSubmit(isLastItem, true);
    } catch (e) {
      console.error(e);
      errorToast(t("combined_products_removal_dialog_error"));
    } finally {
      setCheckoutModal("NONE");
    }
  };

  const removableItemsCount = removableCheckoutItems?.length || 0;

  return (
    <div>
      <DialogHeader>
        <div className="flex h-full w-full flex-row items-center justify-between">
          <h1 className="text-base font-semibold mt-1 text-carbon-dark">
            {t("combined_products_removal_dialog_header")}
          </h1>
          <button className="outline-none">
            <X className="h-5 w-5 cursor-pointer text-coal-dark" onClick={() => setCheckoutModal("NONE")} />
          </button>
        </div>
      </DialogHeader>

      <DialogBody>
        <div className="relative flex w-full flex-col space-y-6 px-6">
          <div className="flex items-start space-x-3">
            <span className="mt-0.5 rounded-full bg-orange-100 p-1">
              <Warning className="h-5 w-5 cursor-pointer text-orange-600" />
            </span>
            <div className="text-sm text-carbon-dark">
              <span className="font-semibold">
                {t(
                  removableItemsCount === 1
                    ? "combined_products_removal_dialog_items_cannot_be_purchased_one"
                    : "combined_products_removal_dialog_items_cannot_be_purchased_other",
                )}
              </span>{" "}
              <span className="font-semibold">{selectedCheckoutItem?.item_title}</span>
            </div>
          </div>
          <div className="rounded-lg bg-red-50 p-4 text-sm">
            <span className="font-semibold text-red-700">
              {t(
                removableItemsCount === 1
                  ? "combined_products_removal_dialog_items_will_be_removed_one"
                  : "combined_products_removal_dialog_items_will_be_removed_other",
              )}
            </span>{" "}
            <span className="text-red-700">
              {t("combined_products_removal_dialog_maintain_offer_integrity")}
            </span>
          </div>
          <div className="max-h-[300px] overflow-y-auto">
            <CheckoutItems items={removableCheckoutItems} checkoutItemsMutable={false} blockVarientSelector={true} />
          </div>

          <div className="flex flex-col gap-3">
            <PrimaryButton
              buttonText={
                Boolean(isLastItem) ? t("remove_last_item_dialog_cta") : t("remove_item_dialog_cta")
              }
              onClick={handleRemoveItems}
              height="h-14"
              isLoading={isLoading}
              isDisabled={false}
            />
            <PrimaryButton
              buttonText={t("keep_existing")}
              onClick={() => setCheckoutModal("NONE")}
              height="h-14"
              className="bg-white text-coal-dark border-2 border-coal-dark"
              isLoading={false}
              isDisabled={false}
            />
          </div>
        </div>
      </DialogBody>
    </div>
  );
};

export default CombinedProductsRemovalDialog;
